<script lang="ts" setup>
import Profile from "@/components/layouts/topBar/Profile.vue";
import MenuComponents from "@/components/layouts/topBar/MenuComponent.vue";
import MobileMenuComponent from "@/components/layouts/topBar/MobileMenuComponent.vue";

import { useLayoutStore } from "@/store/app";
import { SIDEBAR_SIZE, LAYOUTS, LAYOUT_POSITION } from "@/app/const";
import { onMounted, onUnmounted, computed, ref } from "vue";
import { useContextStore } from "@/stores/context.store";

const { SMALL, DEFAULT } = SIDEBAR_SIZE;

const state = useLayoutStore();
const contextStore = useContextStore();
const isSmallMenuActive = ref(false);

const isSmallSideBar = computed(() => {
  return state.sideBarSize === SMALL;
});

const isHorizontal = computed(() => {
  return state.layoutType === LAYOUTS.HORIZONTAL;
});

const isScrollableLayout = computed(() => {
  return state.position === LAYOUT_POSITION.SCROLLABLE;
});

onMounted(() => {
  addEventListeners();
  contextStore.loadContexts();
});

const topBarScrollEvent = () => {
  const pageTopBar = document.getElementById("page-topbar");

  if (pageTopBar && !isScrollableLayout.value) {
    document.body.scrollTop >= 50 || document.documentElement.scrollTop >= 50
      ? pageTopBar.classList.add("topbar-shadow")
      : pageTopBar.classList.remove("topbar-shadow");
  }
};

const addEventListeners = () => {
  document.addEventListener("scroll", topBarScrollEvent);
};

const selectedProgram = computed(() => contextStore.selectedProgram);
const selectedTitle = computed(() => selectedProgram.value?.displayName || selectedProgram.value?.name || "Valorum Copay");
const selectedSubtitle = computed(() => {
  if (selectedProgram.value?.demo) return "Demo workspace";

  return "Patient Access Operations";
});

const onProgramSelect = (program: any) => {
  if (selectedProgram.value?.id === program.id) return;

  contextStore.setSelectedProgram(program);
};

const onDrawerClick = () => {
  if (isHorizontal.value) {
    isSmallMenuActive.value = !isSmallMenuActive.value;
  }

  const sideBarSize = state.sideBarSize;

  if (sideBarSize === SMALL) {
    state.changeSideBarSize(DEFAULT);
  } else {
    state.changeSideBarSize(SMALL);
  }
};

onUnmounted(() => {
  document.removeEventListener("scroll", topBarScrollEvent);
});
</script>

<template>
  <v-app-bar
    :scroll-behavior="isScrollableLayout ? 'hide elevate' : 'elevate'"
    id="page-topbar"
    height="60"
    class="caravel-topbar"
  >
    <v-container class="layout-width caravel-topbar__container" fluid>
      <div class="caravel-topbar__inner">
        <div class="caravel-topbar__left">
          <v-app-bar-nav-icon
            variant="text"
            class="caravel-topbar__hamburger"
            @click="onDrawerClick"
          >
            <div id="topnav-hamburger-icon" class="d-flex align-center">
              <span
                class="hamburger-icon"
                :class="isSmallSideBar ? 'open' : ''"
              >
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </v-app-bar-nav-icon>

          <v-menu width="340" location="bottom start" offset="8">
            <template #activator="{ props }">
              <button type="button" class="caravel-topbar__workspace" v-bind="props">
                <span class="caravel-topbar__workspace-text">
                  <span class="caravel-topbar__title">{{ selectedTitle }}</span>
                  <span class="caravel-topbar__subtitle">{{ selectedSubtitle }}</span>
                </span>
                <span v-if="selectedProgram?.demo" class="caravel-topbar__demo-badge">Demo</span>
                <i class="ph ph-caret-down caravel-topbar__workspace-caret"></i>
              </button>
            </template>

            <v-list density="compact" :lines="false" class="caravel-context-menu" nav>
              <v-list-item v-if="contextStore.loading" class="caravel-context-menu__empty">
                Loading contexts...
              </v-list-item>

              <v-list-item
                v-else-if="!contextStore.clients.length"
                class="caravel-context-menu__empty"
              >
                No contexts available
              </v-list-item>

              <template v-else v-for="program in contextStore.programs" :key="program.id">
                <v-list-item
                  :active="selectedProgram?.id === program.id"
                  class="caravel-context-menu__program"
                  @click="onProgramSelect(program)"
                >
                  <div class="caravel-context-menu__program-row">
                    <span class="caravel-context-menu__program-name">{{ program.displayName || program.name }}</span>
                    <span v-if="program.demo" class="caravel-context-menu__demo">Demo</span>
                  </div>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </div>

        <div class="caravel-topbar__right">
          <Profile />
        </div>
      </div>

      <div
        v-if="isHorizontal && $vuetify.display.mdAndUp"
        class="navbar-menu-horizontal"
      >
        <v-divider class="topbar-divider" />
        <MenuComponents />
      </div>

      <MobileMenuComponent
        v-if="isHorizontal && isSmallMenuActive && $vuetify.display.smAndDown"
      />
    </v-container>
  </v-app-bar>
</template>